import React from "react";
import Alert from "@mui/material/Alert";
import type { ShopCaseDetail, ShopEmailJobSummary } from "./shopAdminApi";

const labels: Record<ShopEmailJobSummary["state"], string> = {
  pending: "Köad", leased: "Förbereds", sending: "Sändning pågår", retry: "Väntar på nytt försök",
  blocked_configuration: "Blockerad av e-postkonfiguration", provider_accepted: "Accepterad av e-postservern",
  failed: "Avvisad av e-postservern", delivery_unknown: "Okänt sändningsresultat", quarantined: "Kräver granskning",
  unknown: "Okänd status", missing: "E-postjobbet saknas",
};
const reasons: Record<string, string> = {
  invalid_job: "Jobbuppgifterna behöver granskas.", invalid_case: "Det ursprungliga ärendet behöver granskas.",
  context_mismatch: "Projekt eller test-/produktionsfas stämmer inte.", configuration_changed: "Avsändarkonfigurationen har ändrats.",
  source_changed: "De ursprungliga ärendeuppgifterna har ändrats.", configuration_unavailable: "SMTP-konfigurationen saknas eller är ogiltig.",
  smtp_temporary_rejection: "E-postservern avvisade försöket tillfälligt. Ett nytt försök är schemalagt.",
  smtp_permanent_rejection: "E-postservern avvisade försöket permanent. Kontrollera orsaken hos e-postleverantören.",
  smtp_outcome_unknown: "Meddelandet kan ha accepterats. Kontrollera leverantörens spårning innan en eventuell ny sändning.",
  sending_lease_expired: "Sändningen saknar sparat slutresultat. Meddelandet kan ha accepterats.",
  retry_exhausted: "Gränsen på åtta sändningsförsök har nåtts.", unknown: "Diagnostiken behöver granskas.",
};
const date = (value: string) => new Date(value).toLocaleString("sv-SE", { timeZone: "Europe/Stockholm" });
function Job({ label, job }: { label: string; job: ShopEmailJobSummary }) {
  const valid = job.integrityIssues.length === 0;
  return <section aria-label={label}>
    <p><strong>{label}:</strong> {labels[job.state]}</p>
    <p>Sändningsförsök: {job.attempts ?? "Okänt"} av 8</p>
    {job.lastAttemptAt && <p>Senaste försök: {date(job.lastAttemptAt)}</p>}
    {job.nextCheckAt && <p>Nästa kontroll: {date(job.nextCheckAt)}</p>}
    {job.acceptedAt && <p>Accepterat: {date(job.acceptedAt)}</p>}
    {!valid && <Alert severity="warning">E-postjobbet innehåller ofullständiga eller motstridiga uppgifter. Granska innan statusen används.</Alert>}
    {valid && job.state === "provider_accepted" && <Alert severity="info">E-postservern har accepterat meddelandet. Leverans till inkorgen är inte verifierad.</Alert>}
    {job.reasonCode && <p>{reasons[job.reasonCode] || reasons.unknown}</p>}
    {["delivery_unknown", "quarantined", "failed", "unknown", "missing"].includes(job.state) &&
      <Alert severity="warning">Manuell granskning krävs. Ingen automatisk omsändning görs.</Alert>}
  </section>;
}
export default function CaseEmailDelivery({ delivery }: { delivery: ShopCaseDetail["emailDelivery"] }) {
  return <div className="WebshopCaseDetail">
    {delivery.configuration !== "configured_unverified" && <Alert severity="warning">
      {delivery.configuration === "disabled" ? "E-postsändning är avstängd. Ärendet är sparat och köade meddelanden väntar."
        : "E-postkonfigurationen är ofullständig. Ärendet är sparat."}</Alert>}
    <Job label="Notifiering till personal" job={delivery.staff} />
    <Job label="Kundens mottagningsbevis" job={delivery.receipt} />
  </div>;
}
